// One module's shell — Phase 05b. The markup main.js createModule built as an innerHTML
// string, now rendered from the store: header (title, pin, close), the player strip
// (PlayerStrip, 05c), the body the widget renders into, and the 8 resize handles.
//
// React owns the element's existence and these nodes; gestures still change the element
// itself (its position every frame of a move, its dimmed look while resizing, and the
// transient classes: dragging, closing, merging, link-skipped…). That's safe:
//   - the class list is fixed at creation; the one class React changes later —
//     `module-selected` (05d) — is flipped on its own with classList, never by rewriting
//     className, so a merge shake or a close animation mid-way is left alone;
//   - the box (left / top / width / height + data-base-cols / rows) comes from the store.
//     A move only reaches it when the gesture ENDS (commitBoxes, 05e-2), with the values
//     the page already has; a resize writes it at its release (05f). React writes a style
//     only when its value changed, so a re-render mid-drag (a pin, a selection) never pulls
//     the module back.

import { memo, useCallback, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { useProtoStore } from "../store/store.js";
import type { PlacedModule as PlacedModuleState } from "../store/types.js";
import { canvasApi, shellInitFor } from "./shell-actions.js";
import { registerModuleElement } from "./module-registry.js";
import { moduleClassName, pixelSize } from "./module-shell.js";
import { PlayerStrip } from "./PlayerStrip.js";
import { useModuleMove } from "./useModuleMove.js";
import { useModuleResize } from "./useModuleResize.js";

// main.js's placeholder until the widget's first render replaces it.
const BODY_PLACEHOLDER = '<div class="module-loading">Loading module HTML…</div>';

/**
 * A ref that fills an element with its starting HTML once. For the body: the widget's
 * own React root (the bridge) takes it over, and React requires a root's container to be
 * a childless leaf in THIS tree — so not JSX children, not dangerouslySetInnerHTML.
 */
function fillOnce(html: string) {
  return (el: HTMLElement | null) => {
    // StrictMode attaches refs twice; only the first fill counts.
    if (el && !el.firstChild) el.innerHTML = html;
  };
}

// In main.js's order: the bottom-right corner first (the original handle), then the rest.
const RESIZE_HANDLES = [
  ["resize-handle", "se"],
  ["resize-handle-nw", "nw"],
  ["resize-handle-ne", "ne"],
  ["resize-handle-sw", "sw"],
  ["resize-handle-n", "n"],
  ["resize-handle-s", "s"],
  ["resize-handle-e", "e"],
  ["resize-handle-w", "w"],
] as const;

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 6 15 12 9 18"></polyline>
    </svg>
  );
}

function PlacedModuleImpl({ module }: { module: PlacedModuleState }) {
  const { id } = module;
  // Creation-time only (see the header): the slug class and the picker's option list.
  const [init] = useState(() => shellInitFor(id));
  const [fillBody] = useState(() => fillOnce(BODY_PLACEHOLDER));

  const elRef = useRef<HTMLDivElement | null>(null);
  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      registerModuleElement(id, el);
    },
    [id],
  );
  const selected = useProtoStore((s) => s.selectedIds.includes(id));
  const onHeaderPointerDown = useModuleMove(elRef);
  const onHandlePointerDown = useModuleResize(elRef);
  useLayoutEffect(() => {
    elRef.current?.classList.toggle("module-selected", selected);
  }, [selected]);
  const modOf = (e: MouseEvent) => (e.currentTarget as HTMLElement).closest<HTMLElement>(".placed-module");

  return (
    <div
      ref={ref}
      className={moduleClassName(init.slug)}
      style={{ left: module.x, top: module.y, width: pixelSize(module.cols), height: pixelSize(module.rows) }}
      data-base-cols={module.cols}
      data-base-rows={module.rows}
      data-current-multiplier="1"
      data-pinned={module.pinned ? "true" : "false"}
    >
      <div className="module-header" onPointerDown={onHeaderPointerDown}>
        <div className="mobile-drag-handle" title="Drag to reorder">
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="7" r="1.5" />
            <circle cx="8" cy="7" r="1.5" />
            <circle cx="2" cy="12" r="1.5" />
            <circle cx="8" cy="12" r="1.5" />
          </svg>
        </div>
        <div className="module-title">{module.title}</div>
        <div className="header-actions">
          <div
            className={module.pinned ? "module-pin pinned" : "module-pin"}
            title="Pin this module"
            onClick={(e) => {
              const mod = modOf(e);
              if (mod) canvasApi().togglePin(mod);
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="17" x2="12" y2="22"></line>
              <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.68V6a3 3 0 1 0-6 0v4.68a2 2 0 0 1-1.11 1.87l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
            </svg>
          </div>
          <div
            className="module-close"
            onClick={(e) => {
              const mod = modOf(e);
              if (mod) canvasApi().closeFromHeader(mod);
            }}
          >
            ×
          </div>
        </div>
      </div>
      {/* Widgets portal their own controls (a season picker…) in after the picker
          (SubControlsSlot); the picker's two nodes never change, so they stay first. */}
      {init.optionsHtml !== null && (
        <div className="module-sub-controls">
          <PlayerStrip optionsHtml={init.optionsHtml} player={module.player} />
        </div>
      )}
      <div className="module-body" ref={fillBody} />
      {RESIZE_HANDLES.map(([cls, dir]) => (
        <div key={dir} className={cls} data-resize-dir={dir} onPointerDown={(e) => onHandlePointerDown(e, dir)}>
          <ChevronIcon />
        </div>
      ))}
    </div>
  );
}

/** Re-renders only when this module's store entry changes (a pin, a title, a player, its box). */
export const PlacedModule = memo(PlacedModuleImpl);
